import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { TechnicalGrid } from '../ui/TechnicalGrid';
import { StatusIndicator } from '../ui/StatusIndicator';
import { Search, Wrench, Users } from 'lucide-react';
export function Process() {
  const steps = [{
    number: '01',
    title: 'Diagnose',
    desc: 'We audit your current systems, identify bottlenecks, and map out the high-impact fixes. No guesswork.',
    icon: Search,
    status: 'processing' as const
  }, {
    number: '02',
    title: 'Build',
    desc: 'We configure your Microsoft environment (SharePoint, Power Automate) to automate workflows. We build it alongside your team.',
    icon: Wrench,
    status: 'online' as const
  }, {
    number: '03',
    title: 'Embed & Enable',
    desc: "We train your team and provide support to ensure adoption. We don't just hand over keys; we ensure it works.",
    icon: Users,
    status: 'online' as const
  }];
  return <section className="relative overflow-hidden bg-dashboard-surface py-16 md:py-24 lg:py-28 border-y border-dashboard-border" data-id="element-200">
      <TechnicalGrid data-id="element-201">
        <Container className="relative z-10" data-id="element-202">
          <motion.div className="mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-203">
            <div className="mb-6" data-id="element-204">
              <span className="text-xs font-mono text-tech-blue uppercase tracking-wider" data-id="element-205">
                // IMPLEMENTATION_PROCESS
              </span>
            </div>
            <h2 className="text-4xl font-black tracking-tight text-dashboard-text sm:text-5xl mb-6" data-id="element-206">
              How We <span className="text-gradient-tech" data-id="element-207">Work</span>
            </h2>
            <p className="text-lg text-dashboard-text leading-relaxed max-w-3xl font-mono" data-id="element-208">
              A simple, transparent process designed to minimize disruption and
              maximize impact.
            </p>
          </motion.div>

          <div className="relative" data-id="element-209">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-tech-blue/20" data-id="element-210"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-211">
              {steps.map((step, index) => <motion.div key={step.number} className="metric-card relative" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15,
              duration: 0.5
            }} data-id="element-212">
                  {/* Step number badge */}
                  <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-tech-blue border-2 border-dashboard-surface flex items-center justify-center" data-id="element-213">
                    <span className="text-sm font-bold text-dashboard-bg font-mono" data-id="element-214">
                      {step.number}
                    </span>
                  </div>

                  {/* Header */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-dashboard-border pt-2" data-id="element-215">
                    <div className="flex items-center gap-3" data-id="element-216">
                      <div className="h-10 w-10 rounded bg-dashboard-bg flex items-center justify-center border border-tech-blue/30" data-id="element-217">
                        <step.icon className="h-5 w-5 text-tech-blue" data-id="element-218" />
                      </div>
                      <div data-id="element-219">
                        <div className="text-xs font-mono text-dashboard-muted" data-id="element-220">
                          PHASE_{index + 1}
                        </div>
                        <div className="text-sm font-bold text-dashboard-text" data-id="element-221">
                          {step.title}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-dashboard-muted leading-relaxed mb-4" data-id="element-222">
                    {step.desc}
                  </p>

                  {/* Status */}
                  <div className="pt-3 border-t border-dashboard-border" data-id="element-223">
                    <StatusIndicator status={step.status} label={step.status} data-id="element-224" />
                  </div>
                </motion.div>)}
            </div>
          </div>
        </Container>
      </TechnicalGrid>
    </section>;
}